// src/lib/api/needApi.ts

import { getApiClient } from './client';
import {
    Need,
    NeedListResponse,
    CreateNeedRequest,
    UpdateNeedRequest,
    UpdateNeedStatusRequest
} from '../models/need';

// ===================================
// NEED OPERATIONS
// ===================================

/**
 * Create new need
 */
const createNeed = async (data: CreateNeedRequest): Promise<Need> => {
    console.log('[needApi] Creating need');
    const result = await getApiClient().post<Need>('/api/needs', data);
    console.log('[needApi] Need created:', result.id);
    return result;
};

/**
 * Get current employer's active needs (for dashboard)
 */
const getMyActiveNeeds = async (page: number = 1, limit: number = 20): Promise<NeedListResponse> => {
    try {
        console.log('[needApi] Fetching my active needs');
        const data = await getApiClient().get<NeedListResponse>('/api/needs/my/active', {
            params: { page, limit }
        });
        console.log('[needApi] Active needs loaded:', data.total);
        return data;
    } catch (error) {
        console.error('[needApi] Error fetching active needs:', error);
        return { needs: [], total: 0 };
    }
};

/**
 * Get need by ID
 */
const getNeedById = async (needId: string): Promise<Need> => {
    console.log('[needApi] Fetching need:', needId);
    const data = await getApiClient().get<Need>(`/api/needs/${needId}`);
    console.log('[needApi] Need loaded:', data.id);
    return data;
};

/**
 * Update need
 */
const updateNeed = async (
    needId: string,
    data: UpdateNeedRequest
): Promise<Need> => {
    console.log('[needApi] Updating need:', needId);
    const result = await getApiClient().put<Need>(`/api/needs/${needId}`, data);
    console.log('[needApi] Need updated:', result.id);
    return result;
};

/**
 * Update only the status of a need
 */
const updateNeedStatus = async (
    needId: string,
    data: UpdateNeedStatusRequest
): Promise<Need> => {
    console.log('[needApi] Updating need status:', needId, data.status);
    const result = await getApiClient().patch<Need>(`/api/needs/${needId}/status`, data);
    console.log('[needApi] Status updated:', result.status);
    return result;
};

/**
 * #281 — reopen an EXPIRED posting with a new future deadline. Dedicated endpoint
 * (not the general PUT, whose partial update would zero the work-location flags).
 * Sets the deadline + flips expired→active atomically on the server.
 */
const relistNeed = async (needId: string, applicationDeadline: string): Promise<Need> => {
    console.log('[needApi] Relisting need:', needId, applicationDeadline);
    return await getApiClient().post<Need>(`/api/needs/${needId}/relist`, {
        application_deadline: applicationDeadline,
    });
};

/**
 * #281 DEV-ONLY — force a posting to expire now (regardless of its deadline), so
 * the expiry → notification → "Att granska" flow can be tested without waiting.
 * The backend 404s this when APP_DEV_MODE is off.
 */
const devExpireNeed = async (needId: string): Promise<Need> => {
    return await getApiClient().post<Need>(`/api/needs/${needId}/dev-expire`, {});
};

/**
 * Delete need (soft delete)
 */
const deleteNeed = async (needId: string): Promise<void> => {
    console.log('[needApi] Deleting need:', needId);
    await getApiClient().delete(`/api/needs/${needId}`);
    console.log('[needApi] Need deleted');
};

// ===================================
// AI GENERATION
// ===================================

// Mirrors the backend services.NeedGenerationRequest / GeneratedNeed.
export interface NeedGenerationRequest {
    topic: string;
    city?: string;
    employment_type?: string;
    employment_extent?: string;
    additional_notes?: string;
    source_ids?: string[];
}

export interface GeneratedNeed {
    role: string;
    description: string;           // rich Markdown (## sections, no H1/meta/competencies list)
    competencies: string[];
    city: string;
    start_date: string;
    min_hours_per_week: number;
    max_hours_per_week: number;
    work_location_on_site: boolean;
    work_location_remote: boolean;
    offers_supervision: boolean;
    application_deadline?: string;
    employment_type?: string;
    employment_extent?: string;
    extent_percent?: number;
    positions_count?: number;
    salary_type?: string;
    salary_min?: number;
    salary_max?: number;
}

/**
 * One-shot AI annons draft (NOT saved) — loaded into the create form.
 */
const aiGenerateNeed = async (data: NeedGenerationRequest): Promise<GeneratedNeed> => {
    console.log('[needApi] AI-generating annons draft');
    const result = await getApiClient().post<GeneratedNeed>('/api/needs/ai-generate', data);
    console.log('[needApi] Annons draft generated for role:', result.role);
    return result;
};

// ── Inline Link description assist ─────────────────────────────────────
export type NeedAssistAction =
    | 'rewrite_formal'
    | 'rewrite_engaging'
    | 'shorten'
    | 'proofread'
    | 'expand'
    | 'custom';

export interface NeedAssistRequest {
    action: NeedAssistAction;
    content: string;         // current markdown / selected excerpt (may be empty)
    instruction?: string;    // free text (expand/custom)
    role?: string;           // optional context
    scope?: 'selection' | 'full'; // 'selection' = revise only the excerpt
}

/**
 * Ask Link to revise a job-posting description. Returns revised markdown
 * (NOT saved) for preview-then-apply.
 */
const aiAssistNeed = async (data: NeedAssistRequest): Promise<string> => {
    const result = await getApiClient().post<{ content: string }>('/api/needs/ai-assist', data);
    return result.content;
};

// ===================================
// ADMIN OPERATIONS
// ===================================

/**
 * List all needs (admin only)
 */
const listAllNeeds = async (page: number = 1, limit: number = 20): Promise<NeedListResponse> => {
    try {
        console.log('[needApi] Fetching all needs (admin)');
        const data = await getApiClient().get<NeedListResponse>('/api/admin/needs', {
            params: { page, limit }
        });
        console.log('[needApi] All needs loaded:', data.total);
        return data;
    } catch (error) {
        console.error('[needApi] Error fetching all needs:', error);
        return { needs: [], total: 0 };
    }
};

export const needApi = {
    // Need operations
    createNeed,
    getMyActiveNeeds,
    getNeedById,
    updateNeed,
    updateNeedStatus,
    relistNeed,
    devExpireNeed,
    deleteNeed,

    // AI generation
    aiGenerateNeed,
    aiAssistNeed,

    // Admin operations
    listAllNeeds,
};
